import { useState } from 'react'
import { Link, useNavigation } from 'react-router-dom'
import { MeHealthCenter, Mode } from '../api/me'
import { useMe } from '../context/MeContext'
import { api } from '../lib/api'
import { resetIdentity, trackEvent } from '../lib/config'
import { MODE_NAMES } from '../lib/mode'
import { ChangeProfessionModal } from './ChangeProfessionModal'
import { DropdownLi } from './common'
import { useImpression } from '../lib/useImpression'
import styles from './Navbar.module.scss'

function getModePath(healthCenter: MeHealthCenter, mode: Mode): string {
  if (mode === 'calendar') {
    return `/health_centers/${healthCenter.id}/box_calendars`
  }
  return `/health_centers/${healthCenter.id}/patients?mode=${mode}`
}

function getPermissionLinks(healthCenter: MeHealthCenter): Mode[] {
  const modes: Mode[] = []
  if (healthCenter.permissions.contability) {
    modes.push('contability')
  }
  if (healthCenter.permissions.attention) {
    modes.push('attention')
  }
  if (healthCenter.permissions.calendar) {
    modes.push('calendar')
  }
  return modes
}

export function NavigationBar() {
  const { state } = useNavigation()
  return state === 'idle' ? null : <div className={styles.navigationBar} data-testid="navigation-bar" />
}

export function Navbar({ healthCenter, currentMode }: { healthCenter: MeHealthCenter | null; currentMode: Mode | null }) {
  const me = useMe()
  const [collapsed, setCollapsed] = useState(true)
  const [changeProfessionOpen, setChangeProfessionOpen] = useState(false)

  const professional = me.professional
  const showPaymentBadge = healthCenter !== null && healthCenter.account_type === 'paid' && !healthCenter.has_current_paid_payment
  const showUpgradeBadge = healthCenter !== null && healthCenter.account_type === 'freemium' && healthCenter.patients_count >= 5
  const badgePrompt = showPaymentBadge ? 'payment_due' : showUpgradeBadge ? 'upgrade' : null
  useImpression('Upgrade Prompt Shown', badgePrompt ? { prompt: badgePrompt, surface: 'navbar' } : null)
  const trackBadgeClick = () => trackEvent('Upgrade Prompt Clicked', { prompt: badgePrompt, surface: 'navbar', target: 'badge' })

  const handleSignOut = async () => {
    await api.delete('/professionals/sign_out')
    Array.from({ length: window.localStorage.length }, (_, index) => window.localStorage.key(index))
      .filter((key): key is string => key?.startsWith('hc-autosave:') ?? false)
      .forEach((key) => window.localStorage.removeItem(key))
    trackEvent('Signed Out', undefined, { transport: 'sendBeacon' })
    resetIdentity()
    window.location.assign('/')
  }

  const userName = healthCenter ? `${professional.name}  -  ${healthCenter.profession.title}` : professional.name

  return (
    <header>
      <nav className="navbar navbar-info navbar-fixed-top" role="navigation">
        <div className="container">
          <div className="navbar-header">
            {healthCenter && healthCenter.has_logo ? (
              <a href={`/health_centers/${healthCenter.id}/patients`} style={{ display: 'inline-block', backgroundColor: 'white' }}>
                <img
                  src={healthCenter.logo_url}
                  className="img-responsive"
                  style={{ marginTop: 3, display: 'block', marginLeft: 'auto', marginRight: 'auto', height: 50 }}
                />
              </a>
            ) : (
              <a
                href={healthCenter ? `/health_centers/${healthCenter.id}/patients` : '/landing'}
                style={{ display: 'inline-block' }}
              >
                <img
                  src="/logo_white.png"
                  className="img-responsive"
                  style={{ marginTop: 3, display: 'block', marginLeft: 'auto', marginRight: 'auto', height: 50 }}
                />
              </a>
            )}
            <button className="navbar-toggle" type="button" onClick={() => setCollapsed(!collapsed)}>
              <i className="fa fa-bars" />
            </button>
          </div>
          <div className={`navbar-collapse collapse${collapsed ? '' : ' in'}`}>
            <p className="navbar-text hidden-xs hidden-sm" style={{ color: 'white' }}>
              {userName}
            </p>
            <ul className="nav navbar-nav navbar-right">
              {healthCenter &&
                getPermissionLinks(healthCenter).map((mode) => (
                  <li key={mode}>
                    <a
                      href={getModePath(healthCenter, mode)}
                      onClick={() => trackEvent('Mode Switched', { from: currentMode, to: mode }, { transport: 'sendBeacon' })}
                    >
                      {MODE_NAMES[mode]}
                    </a>
                  </li>
                ))}
              {healthCenter && professional.admin && (
                <DropdownLi toggle={<>Admin Actions <b className="caret" /></>}>
                  <li>
                    <a
                      href="#"
                      onClick={(event) => {
                        event.preventDefault()
                        setChangeProfessionOpen(true)
                      }}
                    >
                      Cambiar profesión
                    </a>
                  </li>
                </DropdownLi>
              )}
              {healthCenter && me.health_centers.length > 1 && (
                <li className="waves-effect waves-blue">
                  <Link to="/health_centers">Centros Médicos</Link>
                </li>
              )}
              {healthCenter && (
                <DropdownLi
                  toggle={
                    <>
                      Mi cuenta <b className="caret" />
                      {showPaymentBadge && (
                        <span className="badge badge-pill badge-light" style={{ textTransform: 'initial' }} onClick={trackBadgeClick}>
                          Pago disponible
                        </span>
                      )}
                      {!showPaymentBadge && showUpgradeBadge && (
                        <span className="badge badge-pill badge-light" style={{ textTransform: 'initial' }} onClick={trackBadgeClick}>
                          Subir de plan
                        </span>
                      )}
                    </>
                  }
                >
                  <li>
                    <a
                      href={`/professionals/health_centers/${healthCenter.id}/edit`}
                      onClick={() => {
                        trackEvent('Settings Opened', { section: 'profile', source: 'navbar' }, { transport: 'sendBeacon' })
                        if (badgePrompt) {
                          trackEvent('Upgrade Prompt Clicked', { prompt: badgePrompt, surface: 'navbar', target: 'profile_link' }, { transport: 'sendBeacon' })
                        }
                      }}
                    >
                      Mi perfil
                      {showPaymentBadge && <span className="badge badge-pill badge-light">Paga aquí</span>}
                      {!showPaymentBadge && showUpgradeBadge && <span className="badge badge-pill badge-light">Aquí</span>}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      onClick={(event) => {
                        event.preventDefault()
                        void handleSignOut()
                      }}
                    >
                      Cerrar sesión
                    </a>
                  </li>
                </DropdownLi>
              )}
              {healthCenter && (
                <DropdownLi toggle={<>¿Necesitas ayuda? <b className="caret" /></>}>
                  <li className="dropdown waves-effect waves-blue">
                    <a href="mailto:contacto@historiaclinica.cl" onClick={() => trackEvent('Help Opened', { item: 'email' })}>
                      contacto@historiaclinica.cl
                    </a>
                  </li>
                  <li className="dropdown waves-effect waves-blue">
                    <a href="/landing#faq" onClick={() => trackEvent('Help Opened', { item: 'faq' }, { transport: 'sendBeacon' })}>
                      Preguntas Frecuentes
                    </a>
                  </li>
                  <li className="dropdown waves-effect waves-blue">
                    <a href="/manual.pdf" onClick={() => trackEvent('Help Opened', { item: 'manual' }, { transport: 'sendBeacon' })}>
                      Manual en PDF
                    </a>
                  </li>
                </DropdownLi>
              )}
            </ul>
          </div>
        </div>
        <NavigationBar />
      </nav>
      {changeProfessionOpen && healthCenter && (
        <ChangeProfessionModal healthCenterId={healthCenter.id} onClose={() => setChangeProfessionOpen(false)} />
      )}
    </header>
  )
}
